import React from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { PremiumFallbackMode } from "types/premium.types"
import { PREMIUM_FALLBACK_MODE } from "constants/premium"
import { handlePremiumOnboarding } from "utils/componentsUtils"
import { usePremiumAccess } from "context/PremiumAccessProvider"
import { useAppSelector } from "appRedux/hooks"

type WithPremiumPermissionProps = {
  feature: string;
  constraint?: any;
  constraintType?: string;
  mode?: PremiumFallbackMode;
  fallback?: React.ReactNode;
}

export default function withPremiumPermission<P extends object>(WrappedComponent: React.ComponentType<P>) {
  function WithPremiumPermission(props: P & WithPremiumPermissionProps) {
    const { feature, constraint, constraintType, mode = PREMIUM_FALLBACK_MODE.ONBOARD, fallback = null, ...otherProps } = props

    const activePremiumFeatures = useAppSelector((state) => state.premium.activePremiumFeatures)

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { checkAccess } = usePremiumAccess()

    const hasAccess = checkAccess(feature as keyof typeof activePremiumFeatures, constraint)

    function handleOnBoardModeComponentClicked(event: React.MouseEvent<HTMLDivElement>) {
      const premiumFeature = constraint && constraintType ? constraintType : feature
      handlePremiumOnboarding(event, premiumFeature, dispatch)
    }

    if (!hasAccess) {
      switch (mode) {
        case PREMIUM_FALLBACK_MODE.ONBOARD:
          return <WrappedComponent {...props} onClick={handleOnBoardModeComponentClicked}/>
        case PREMIUM_FALLBACK_MODE.SILENT:
          return <>{fallback}</>
        case PREMIUM_FALLBACK_MODE.REDIRECT:
          navigate("/")
          return <>{fallback}</>
        default:
          console.error(`Unsupported mode for withPremiumPermission HOC. mode: ${mode}`)
          return null
      }
    }

    return <WrappedComponent {...(otherProps as P)} />
  }

  return WithPremiumPermission
}
